/**
 * Assign a flat, ordered list of ingredients to their section groups.
 *
 * Lives in `utils/` because the step-ingredient resolver uses it too, to scope
 * "tous les ingrédients" to one section; `src/utils/` must not import from
 * `src/components/`. The recipe-detail path re-exports it.
 *
 * Assign a flat, ordered list of ingredients to their section groups for display.
 *
 * Two modes:
 *  - **positional** (preferred): when the groups' item counts sum to the ingredient
 *    count, ingredients are sliced by group in order. This is robust to the same
 *    ingredient repeating across sections (e.g. salt/pepper/brown sugar in NYT and
 *    Ricardo recipes) — matching by ingredient NAME would collapse every copy into
 *    whichever group appears last.
 *  - **text** (fallback): when counts don't reconcile (partially-grouped or legacy
 *    data), match each ingredient to a group by normalized text; unmatched
 *    ingredients are returned as `ungrouped` (rendered before the grouped blocks).
 *
 * Ingredient rows that are themselves a group-header line (some scraped lists embed
 * the header as a quantity-less row) are dropped from the output.
 */
export interface IngredientGroupInput {
  name: string;
  items?: string[];
}

export interface GroupedBlock<T> {
  name: string;
  items: T[];
}

export interface GroupingResult<T> {
  mode: 'positional' | 'text';
  ungrouped: T[];
  groups: GroupedBlock<T>[];
}

const normalize = (s: string | undefined): string => (s || '').trim().toLowerCase();

export function groupIngredientsForDisplay<T extends { original?: string; name?: string }>(
  ingredients: T[],
  groups: IngredientGroupInput[],
): GroupingResult<T> {
  const groupNames = new Set<string>(groups.map(g => normalize(g.name)));

  // Drop rows that are themselves a group header.
  const content = ingredients.filter(ing => {
    const original = ing.original || ing.name || '';
    return !(groupNames.has(normalize(original)) || groupNames.has(normalize(ing.name)));
  });

  const nonEmptyGroups = groups.filter(g => (g.items?.length ?? 0) > 0);
  const totalGroupItems = nonEmptyGroups.reduce((sum, g) => sum + (g.items?.length ?? 0), 0);

  // Positional assignment.
  if (nonEmptyGroups.length > 0 && totalGroupItems === content.length) {
    const out: GroupedBlock<T>[] = [];
    let cursor = 0;
    for (const g of nonEmptyGroups) {
      const count = g.items?.length ?? 0;
      const items = content.slice(cursor, cursor + count);
      cursor += count;
      if (items.length > 0) out.push({ name: g.name, items });
    }
    return { mode: 'positional', ungrouped: [], groups: out };
  }

  // Text-matching fallback.
  const itemToGroup = new Map<string, string>();
  for (const g of groups) {
    for (const item of g.items || []) {
      const key = normalize(item);
      if (key) itemToGroup.set(key, g.name);
    }
  }
  const byGroup = new Map<string, T[]>();
  const ungrouped: T[] = [];
  for (const ing of content) {
    const original = ing.original || ing.name || '';
    const groupName = itemToGroup.get(normalize(original)) ?? itemToGroup.get(normalize(ing.name));
    if (groupName) {
      if (!byGroup.has(groupName)) byGroup.set(groupName, []);
      byGroup.get(groupName)!.push(ing);
    } else {
      ungrouped.push(ing);
    }
  }
  const out: GroupedBlock<T>[] = [];
  for (const g of groups) {
    const items = byGroup.get(g.name) || [];
    if (items.length > 0) out.push({ name: g.name, items });
  }
  return { mode: 'text', ungrouped, groups: out };
}
